import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo, useCallback } from "react"

import { Link, useNavigate } from "react-router-dom"

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb2"
import TableContainer from "../../components/Common/TableContainer2"
import { withTranslation } from "react-i18next"

import { get, del } from "helpers/api_helper"
import { checkScope } from "helpers/utility_helper"
import "../../components/Common/datatables.scss"
import Spinners from "components/Common/Spinner"
import moment from "moment"
import MyTooltip from "components/MyTooltip"
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from "reactstrap"
import { toast } from "react-toastify"
import * as FileSaver from "file-saver"
import * as XLSX from "xlsx"

const DeleteCampaignModal = ({ show, onDeleteClick, onCloseClick }) => {
  return (
    <Modal size="md" isOpen={show} toggle={onCloseClick} centered={true}>
      <ModalBody className="px-4 py-4">
        <h5 className="mb-4" style={{ fontWeight: "600" }}>Are you sure to delete?</h5>
        
        <div className="d-flex gap-2 justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            style={{ 
              minWidth: "100px", 
              backgroundColor: "#fff", 
              border: "1px solid #ced4da",
              color: "#495057",
              borderRadius: "8px"
            }}
            onClick={onDeleteClick}
          >
            Yes
          </button>
          <button
            type="button"
            className="btn btn-success"
            style={{ 
              minWidth: "100px",
              backgroundColor: "#34c38f",
              border: "1px solid #34c38f",
              borderRadius: "8px"
            }}
            onClick={onCloseClick}
          >
            No
          </button>
        </div>
      </ModalBody>
    </Modal>
  )
}

const SurveyOrderCampaignList = props => {
  const navigate = useNavigate()
  const [feedbackModal, setFeedbackModal] = useState(false)
  const [feedbackContent, setFeedbackContent] = useState("")
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [exportingId, setExportingId] = useState(null)

  const toggleFeedbackModal = () => setFeedbackModal(!feedbackModal)

  const handleFeedbackClick = (feedback) => {
    setFeedbackContent(feedback || "No feedback details available.")
    setFeedbackModal(true)
  }

  // Badge style helper
  const badgeStyle = (bgColor, textColor, borderColor) => ({
    backgroundColor: bgColor,
    color: textColor,
    border: `1px solid ${borderColor}`,
    padding: "4px 16px",
    borderRadius: "20px",
    fontWeight: "600",
    fontSize: "13px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px"
  })

  // Order Status Badge
  const getOrderStatusBadge = (status, feedback) => {
    if (!status) return <span className="badge" style={badgeStyle("#f5f5f5", "#616161", "#e0e0e0")}>-</span>
    
    // Normalize status for comparison
    const normalizedStatus = status.toLowerCase()
    
    switch (normalizedStatus) {
      case "approved":
        return <span className="badge" style={badgeStyle("#e8f5e9", "#2e7d32", "#c8e6c9")}>Approved</span>
      case "in-progress":
      case "in progress":
        return <span className="badge" style={badgeStyle("#eef6ff", "#2563eb", "#d1e5ff")}>In Progress</span>
      case "pending":
        return <span className="badge" style={badgeStyle("#fffbe6", "#d4a106", "#ffe58f")}>Pending</span>
      case "cancelled":
        return <span className="badge" style={badgeStyle("#fff4eb", "#bc4c2e", "#ffdcc3")}>Cancelled</span>
      case "feedback":
        return (
          <div className="d-flex flex-column align-items-center">
            <span className="badge" style={badgeStyle("#fff4eb", "#bc4c2e", "#ffdcc3")}>
              Feedback <i className="bx bx-error-circle" style={{ fontSize: "16px" }}></i>
            </span>
            <div 
              style={{ fontSize: "12px", color: "#000", cursor: "pointer", textDecoration: "underline", marginTop: "4px", fontWeight: "500" }}
              onClick={() => handleFeedbackClick(feedback)}
            >
              Click for detail
            </div>
          </div>
        )
      case "reject":
        return <span className="badge" style={badgeStyle("#fff1f0", "#cf1322", "#ffa39e")}>Reject</span>
      case "draft":
        return <span className="badge" style={badgeStyle("#f5f5f5", "#595959", "#d9d9d9")}>Draft</span>
      default:
        const displayStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        return <span className="badge" style={badgeStyle("#f5f5f5", "#595959", "#d9d9d9")}>{displayStatus}</span>
    }
  }

  // Campaign Status Badge
  const getCampaignStatusBadge = (status) => {
    switch (status) {
      case "On-going":
        return <span className="badge" style={badgeStyle("#e3f2fd", "#1565c0", "#64b5f6")}>On-going</span>
      case "End - Quota Reached":
        return <span className="badge" style={badgeStyle("#e8f5e9", "#2e7d32", "#81c784")}>End - Quota Reached</span>
      case "End - On Time":
        return <span className="badge" style={badgeStyle("#e8f5e9", "#2e7d32", "#81c784")}>End - On Time</span>
      case "End - Early":
        return <span className="badge" style={badgeStyle("#e8f5e9", "#2e7d32", "#81c784")}>End - Early</span>
      case "Not Yet":
        return <span className="badge" style={badgeStyle("#f5f5f5", "#616161", "#bdbdbd")}>Not Yet</span>
      default:
        return <span className="badge" style={badgeStyle("#f5f5f5", "#616161", "#bdbdbd")}>{status}</span>
    }
  }

  const deleteRow = useCallback(
    id => () => {
      setSelectedId(id)
      setDeleteModal(true)
    },
    []
  )

  const handleDeleteCampaign = async () => {
    if (!selectedId) return
    try {
      const result = await del(
        process.env.REACT_APP_BACKEND_URL + "/v1/admin/campaign/" + selectedId
      )
      if (result && (Number(result.status) === 200 || result.status === "OK")) {
        setDeleteModal(false)
        toast.success("Campaign deleted successfully")
        // Update the list locally instead of reloading the whole page
        setContentList(prevList => prevList.filter(item => item.id !== selectedId))
      } else {
        setDeleteModal(false)
        toast.error("Failed to delete: " + (result?.message || "Unknown error"))
      }
    } catch (error) {
      console.error("Delete error:", error)
      setDeleteModal(false)
      toast.error("Error deleting campaign")
    }
  }

  const handleDuplicate = (id) => {
    navigate(`/survey-order-campaign/duplicate/${id}`)
  }

  const handleExport = async (id) => {
    try {
      setExportingId(id)
      const result = await get(
        `${process.env.REACT_APP_BACKEND_URL}/v1/admin/campaign/${id}/export-links`
      )

      if (result && result.data && result.data.links && Array.isArray(result.data.links)) {
        const excelData = result.data.links.map(link => ({
          ID: link.id,
          UUID: link.uuid,
          URL: link.url,
          "Bucket ID": link.bucket_id,
          "Demographic Group Index": link.demographic_group_index,
          "Create Date": moment(link.create_date).format("YYYY-MM-DD HH:mm:ss"),
        }))

        const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
        const fileExtension = ".xlsx"
        const fileName = `campaign_${id}_export_${moment().format("YYYYMMDD_HHmmss")}`

        const ws = XLSX.utils.json_to_sheet(excelData)
        const wb = { Sheets: { data: ws }, SheetNames: ["data"] }
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" })
        const data = new Blob([excelBuffer], { type: fileType })
        FileSaver.saveAs(data, fileName + fileExtension)
      } else {
        toast.warning("No data available to export")
      }
    } catch (error) {
      console.error("Error exporting Excel:", error)
      toast.error("System error, Please try again.")
    } finally {
      setExportingId(null)
    }
  }

  const columns = useMemo(() => [
    {
      header: "#",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return (
          <div style={{ display: "flex", justifyContent: "center" }}>
            {parseInt(cellProps.row.id) + 1}
          </div>
        )
      },
    },
    {
      header: "Campaign Name",
      accessorKey: "campaign_name",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Survey Name",
      accessorKey: "survey_name",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Panel Select",
      accessorKey: "panel_option",
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      header: "Start Date",
      accessorKey: "start_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.start_date 
          ? moment.utc(cellProps.row.original.start_date).format("YYYY-MM-DD")
          : "-"
      },
    },
    {
      header: "End Date",
      accessorKey: "end_date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.end_date 
          ? moment.utc(cellProps.row.original.end_date).format("YYYY-MM-DD")
          : "-"
      },
    },
    {
      header: "Estimate Total",
      accessorKey: "estimate_cost_total",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return cellProps.row.original.estimate_cost_total 
          ? `${cellProps.row.original.estimate_cost_total} ${cellProps.row.original.estimate_cost_unit || "RMB"}`
          : "-"
      },
    },
    {
      header: "Order Status",
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return getOrderStatusBadge(
          cellProps.row.original.status,
          cellProps.row.original.feedback
        )
      },
    },
    {
      header: "Campaign Status",
      accessorKey: "campaign_status",
      enableColumnFilter: false,
      enableSorting: true,
      cell: cellProps => {
        return getCampaignStatusBadge(cellProps.row.original.campaign_status)
      },
    },
    {
      header: "Tools",
      accessorKey: "tools",
      enableColumnFilter: false,
      enableSorting: false,
      cell: cellProps => {
        let _campaignId = cellProps.row.original.id
        const status = cellProps.row.original.status?.toLowerCase() || ""
        const canEditDelete = ["draft", "pending", "feedback"].includes(status)
        const btnStyle = {
          width: "32px",
          height: "32px",
          padding: "0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }
        return (
          <div className="d-flex gap-1">
            {/* View */}
            <MyTooltip label="View">
              <Link
                className="btn btn-sm btn-info"
                style={btnStyle}
                to={`/survey-order-campaign/view/${_campaignId}`}
              >
                <i className="bx bx-show"></i>
              </Link>
            </MyTooltip>

            {/* Edit */}
            {checkScope("surveyOrderCampaign:update") && canEditDelete && (
              <MyTooltip label="Edit">
                <Link
                  className="btn btn-sm btn-warning"
                  style={btnStyle}
                  to={`/survey-order-campaign/edit/${_campaignId}`}
                >
                  <i className="bx bx-edit-alt"></i>
                </Link>
              </MyTooltip>
            )}

            {/* Duplicate */}
            <MyTooltip label="Duplicate">
              <button
                className="btn btn-sm btn-secondary"
                style={btnStyle}
                onClick={() => handleDuplicate(_campaignId)}
              >
                <i className="bx bx-copy"></i>
              </button>
            </MyTooltip>

            {/* Export */}
            {status !== "draft" && (
              <MyTooltip label="Export">
                <button
                  className="btn btn-sm btn-success"
                  style={btnStyle}
                  disabled={exportingId === _campaignId}
                  onClick={() => handleExport(_campaignId)}
                >
                  {exportingId === _campaignId
                    ? <Spinner size="sm" color="light" />
                    : <i className="bx bx-export"></i>
                  }
                </button>
              </MyTooltip>
            )}

            {/* Delete */}
            {canEditDelete && (
              <button
                className="btn btn-sm btn-danger"
                style={btnStyle}
                onClick={() => {
                  setSelectedId(_campaignId)
                  setDeleteModal(true)
                }}
              >
                <i className="bx bx-trash"></i>
              </button>
            )}
          </div>
        )
      },
    },
  ], [checkScope])

  const [contentList, setContentList] = useState([])
  const [contentListLoading, setContentListLoading] = useState(false)

  useEffect(() => {
    setContentListLoading(true)
    const fetchContentList = async () => {
      try {
        const result = await get(
          process.env.REACT_APP_BACKEND_URL + "/v1/admin/campaign"
        )

        // The API returns { status, message, data: [], pagination: {} }
        const sortedData = (result.data || []).sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at)
        })

        setContentList(sortedData)
      } catch (error) {
        console.log("error", error)
        setContentList([])
      }
      setContentListLoading(false)
    }

    fetchContentList()
  }, [])

  //meta title
  document.title = "Survey Order Campaign | " + process.env.REACT_APP_CMS_TITLE

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title={props.t("Survey Order Campaign")}
            breadcrumbItems={[
              { title: "Survey Order Campaign", link: "survey-order-campaign" },
              { title: "List", link: "list" },
            ]}
          />

          <div className="bInnerContent">
            {contentListLoading ? (
              <Spinners setLoading={setContentListLoading} />
            ) : (
              <TableContainer
                isAddButton={true}
                addRowLink={`/create`}
                columns={columns}
                data={contentList || []}
                isGlobalFilter={true}
                isPagination={true}
                SearchPlaceholder="Search"
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                rootMenu="surveyOrderCampaign"
              />
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteCampaignModal
        show={deleteModal}
        onDeleteClick={handleDeleteCampaign}
        onCloseClick={() => setDeleteModal(false)}
      />

      {/* Feedback Modal */}
      <Modal isOpen={feedbackModal} toggle={toggleFeedbackModal} centered>
        <ModalHeader toggle={toggleFeedbackModal}>
          Feedback Details
        </ModalHeader>
        <ModalBody>
          <p>{feedbackContent}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleFeedbackModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

SurveyOrderCampaignList.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(SurveyOrderCampaignList)
